import slugify from 'slugify';

/**
 * Definition of a filter option that is intended to be extended by concrete
 * filter options.
 */
interface BaseFilterOption<T> {
  id: string;
  name: string;
  value: T;
  selected: boolean;
}

/**
 * A simple filter option with a string or numeric value.
 */
export interface SimpleFilterOption extends BaseFilterOption<string | number> {}

/**
 * A range filter option with minimum and maximum values.
 */
export interface RangeFilterOption extends BaseFilterOption<{ min?: number; max?: number }> {
  label?: string;
  inputLabels?: { min: string; max: string };
  validations?: {
    minRegex?: string;
    maxRegex?: string;
    error?: string;
  };
}

export type FilterOption = SimpleFilterOption | RangeFilterOption;

/**
 * Creates a {@link SimpleFilterOption} with the given values.
 *
 * @param group The name of the encompassing filter.
 * @param name The name of the option.
 * @param value The value of the option.
 * @param selected Whether the option is selected or not.
 */
export function createSimpleFilterOption({
  group,
  name,
  value,
  selected,
}: {
  group: string;
  name: string;
  value: string;
  selected: boolean;
}): SimpleFilterOption {
  return {
    id: slugify(`${group} ${name}`, { lower: true }),
    name,
    value,
    selected,
  };
}

/**
 * Creates a {@link RangeFilterOption} with the given values.
 *
 * @param group The name of the encompassing filter.
 * @param range The range of min and max values in the option.
 * @param label An optional label to apply to the filter.
 * @param inputLabels Optional labels to apply to the min and max.
 * @param validate An optional function for validating the range.
 */
export function createRangeFilterOption({
  group,
  range,
  label,
  inputLabels,
  validations,
}: {
  group: string;
  range: { min?: number; max?: number };
  label?: string;
  inputLabels?: { min: string; max: string };
  validations?: {
    minRegex?: string;
    maxRegex?: string;
    error?: string;
  };
}): RangeFilterOption {
  const { min, max } = range;
  let name: string = '';
  if (!!min && !!max) {
    name = min === max ? min.toString() : `${min} - ${max}`;
  } else if (!!min) {
    name = min.toString();
  } else if (!!max) {
    name = max.toString();
  }

  return {
    id: slugify(group, { lower: true }),
    name,
    ...(label && { label }),
    ...(inputLabels && { inputLabels }),
    ...(validations && { validations }),
    value: {
      min,
      max,
    },
    selected: !!min || !!max,
  };
}
